import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Masonry from "react-masonry-css";
import InfiniteScroll from "react-infinite-scroll-component";
import { clearPosts, fetchPosts } from "../redux/actions/post";
import PostWidgetRedux from "./PostWidgetRedux";
import AuthenticationPrompt from "../components/Profile/AuthenticationPrompt";
import Loader from "../components/Layout/Loader";
import axios from "axios";
import { adsServer, server } from "../server";
import ProductCard from "../components/Route/ProductCard/ProductCard";
import EventCard from "../components/Events/EventCard";
import DualProductAdCarouselProps from "../components/Sponsored/DualProductAdCarouselProps";
import QuadProductAdCarouselProps from "../components/Sponsored/QuadProductCarouselProps";
import SponsoredProductCarouselProps from "../components/Sponsored/SponsoredProductCarouselProps";
import SponsoredBrandCarouselProps from "../components/Sponsored/SponsoredBrandCarouselProps";
import SponsoredShopsCarouselProps from "../components/Sponsored/SponsoredShopsCarouselProps";
import TopBannerAdProps from "../components/Sponsored/TopBannerAdProps";
import BannerAdProps from "../components/Sponsored/BannerAdProps";
import "../stylesheets/BentoFeed.css";

const DEFAULT_LAYOUT_CONFIG = {
  initialBatchSize: 10,
  batchSize: 8,
  adInterval: 6,
};

const BENTO_BREAKPOINTS = {
  default: 2,
  1280: 2,
  900: 1,
  640: 1,
};

const shuffleArray = (items) => {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }
  return shuffled;
};

const weightItems = (items) => {
  const now = Date.now();
  return shuffleArray(items).map((item) => {
    const createdAt = new Date(item.data?.createdAt || now).getTime();
    const ageInDays = Math.min((now - createdAt) / (1000 * 60 * 60 * 24), 21);
    return {
      ...item,
      weight: (1 / (1 + ageInDays)) + 0.1 + Math.random() * 0.2,
    };
  });
};

const interleaveWeightedQueues = (queues) => {
  const workingQueues = queues.map((queue) => [...queue]);
  const orderedItems = [];

  while (workingQueues.some((queue) => queue.length > 0)) {
    const candidates = workingQueues
      .map((queue, queueIndex) => {
        const item = queue[0];
        if (!item) {
          return null;
        }

        return {
          queueIndex,
          item,
          weight: item.weight || 0.1,
        };
      })
      .filter(Boolean);

    const totalWeight = candidates.reduce((sum, candidate) => sum + candidate.weight, 0);
    let threshold = Math.random() * totalWeight;
    let selectedCandidate = candidates[candidates.length - 1];

    for (const candidate of candidates) {
      threshold -= candidate.weight;
      if (threshold <= 0) {
        selectedCandidate = candidate;
        break;
      }
    }

    orderedItems.push(selectedCandidate.item);
    workingQueues[selectedCandidate.queueIndex].shift();
  }

  return orderedItems;
};

const BentoFeed = ({
  ownerId,
  profileType,
  isProfile = false,
  layoutConfig = DEFAULT_LAYOUT_CONFIG,
}) => {
  const dispatch = useDispatch();
  const { posts, fetchPostsLoading, error, totalPages } = useSelector((state) => state.posts);
  const { allProducts = [] } = useSelector((state) => state.products);

  const [currentPage, setCurrentPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(layoutConfig.initialBatchSize || DEFAULT_LAYOUT_CONFIG.initialBatchSize);
  const [communityEvents, setCommunityEvents] = useState([]);
  const [sponsoredCollections, setSponsoredCollections] = useState({
    productAds: [],
    shopAds: [],
    brandAds: [],
    customAds: [],
  });

  const mergedLayoutConfig = useMemo(
    () => ({ ...DEFAULT_LAYOUT_CONFIG, ...layoutConfig }),
    [layoutConfig]
  );

  useEffect(() => {
    dispatch(clearPosts());
    setCurrentPage(1);
    setVisibleCount(mergedLayoutConfig.initialBatchSize);
    dispatch(fetchPosts(profileType, ownerId, isProfile, 1));
  }, [dispatch, mergedLayoutConfig.initialBatchSize, ownerId, profileType, isProfile]);

  useEffect(() => {
    const fetchSponsoredCollections = async () => {
      try {
        const response = await axios.get(`${adsServer}/sponsored/sponsored-sidebar-ads`, {
          withCredentials: true,
        });
        const ads = Array.isArray(response.data) ? response.data : [];

        setSponsoredCollections({
          productAds: ads.filter((ad) => ad.type === "product"),
          shopAds: ads.filter((ad) => ad.type === "shop"),
          brandAds: ads.filter((ad) => ad.type === "brand"),
          customAds: ads.filter((ad) => ad.type === "custom"),
        });
      } catch (error) {
        console.error("Error fetching sponsored collections:", error);
      }
    };

    fetchSponsoredCollections();
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${server}/event/events`, {
          withCredentials: true,
        });
        setCommunityEvents(Array.isArray(response.data) ? response.data : response.data?.events || []);
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };
    fetchEvents();
  }, []);

  const productFeedItems = useMemo(() => {
    if (!Array.isArray(allProducts)) {
      return [];
    }

    return allProducts
      .filter((product) => {
        if (!product) {
          return false;
        }

        if (Array.isArray(product.productType) && product.productType.length > 0) {
          return product.productType.includes("retail");
        }

        return true;
      })
      .slice(0, 24)
      .map((product) => ({ type: "product", data: product }));
  }, [allProducts]);

  const postFeedItems = useMemo(
    () => (Array.isArray(posts) ? posts.map((post) => ({ type: "post", data: post })) : []),
    [posts]
  );

  const eventFeedItems = useMemo(
    () =>
      (Array.isArray(communityEvents) ? communityEvents : [])
        .slice(0, 18)
        .map((event) => ({ type: "event", data: event })),
    [communityEvents]
  );

  const mixedFeedItems = useMemo(() => {
    const weightedQueues = [
      weightItems(postFeedItems),
      weightItems(productFeedItems),
      weightItems(eventFeedItems),
    ].filter((queue) => queue.length > 0);

    const interleavedItems = interleaveWeightedQueues(weightedQueues);
    const adWidgets = [];

    if (sponsoredCollections.productAds.length >= 2) {
      adWidgets.push({
        id: "dual-product",
        render: (key) => <DualProductAdCarouselProps key={key} ads={sponsoredCollections.productAds.slice(0, 2)} />,
      });
    }

    if (sponsoredCollections.productAds.length >= 4) {
      adWidgets.push({
        id: "quad-product",
        render: (key) => <QuadProductAdCarouselProps key={key} ads={sponsoredCollections.productAds.slice(0, 4)} />,
      });
    }

    if (sponsoredCollections.productAds.length > 0) {
      adWidgets.push({
        id: "product-carousel",
        render: (key) => <SponsoredProductCarouselProps key={key} ads={sponsoredCollections.productAds} />,
      });
      adWidgets.push({
        id: "top-banner",
        render: (key) => <TopBannerAdProps key={key} ad={sponsoredCollections.productAds[0]} />,
      });
      adWidgets.push({
        id: "banner",
        render: (key) => <BannerAdProps key={key} ad={sponsoredCollections.productAds[1] || sponsoredCollections.productAds[0]} />,
      });
    }

    if (sponsoredCollections.brandAds.length > 0) {
      adWidgets.push({
        id: "brand-carousel",
        render: (key) => <SponsoredBrandCarouselProps key={key} ads={sponsoredCollections.brandAds} />,
      });
    }

    if (sponsoredCollections.shopAds.length > 0) {
      adWidgets.push({
        id: "shop-carousel",
        render: (key) => <SponsoredShopsCarouselProps key={key} ads={sponsoredCollections.shopAds} />,
      });
    }

    if (adWidgets.length === 0) {
      return interleavedItems;
    }

    const itemsWithAds = [];
    let adIndex = 0;
    interleavedItems.forEach((item, index) => {
      itemsWithAds.push(item);

      if ((index + 1) % mergedLayoutConfig.adInterval === 0) {
        const widget = adWidgets[adIndex % adWidgets.length];
        itemsWithAds.push({
          type: "ad",
          data: {
            id: `${widget.id}-${index}`,
            render: widget.render,
          },
        });
        adIndex += 1;
      }
    });

    return itemsWithAds;
  }, [communityEvents, eventFeedItems, mergedLayoutConfig.adInterval, postFeedItems, productFeedItems, sponsoredCollections]);

  const displayedItems = useMemo(
    () => mixedFeedItems.slice(0, visibleCount),
    [mixedFeedItems, visibleCount]
  );

  const hasMoreItems = displayedItems.length < mixedFeedItems.length || currentPage < (totalPages || 1);

  const handleLoadMore = () => {
    const nextVisibleCount = visibleCount + mergedLayoutConfig.batchSize;
    const shouldFetchNextPage =
      nextVisibleCount >= mixedFeedItems.length - 2 &&
      !fetchPostsLoading &&
      currentPage < (totalPages || 1);

    if (shouldFetchNextPage) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      dispatch(fetchPosts(profileType, ownerId, isProfile, nextPage));
    }

    setVisibleCount(nextVisibleCount);
  };

  if (error) {
    const errorMsg = typeof error === "string" ? error : error?.message || JSON.stringify(error);
    if (errorMsg.includes("401")) {
      return <AuthenticationPrompt forceVisible />;
    }
    return <div className="bento-feed-error">Error loading feed: {errorMsg}</div>;
  }

  if (fetchPostsLoading && currentPage === 1 && displayedItems.length === 0) {
    return <Loader />;
  }

  if (mixedFeedItems.length === 0) {
    return (
      <div className="bento-feed-empty">
        <p>The home board will populate here as posts, products, events, and ads become available.</p>
      </div>
    );
  }

  return (
    <div className="bento-feed-container">
      <InfiniteScroll
        dataLength={displayedItems.length}
        next={handleLoadMore}
        hasMore={hasMoreItems}
        loader={<div className="bento-feed-loader"><Loader /></div>}
      >
        <Masonry
          breakpointCols={BENTO_BREAKPOINTS}
          className="my-masonry-grid home-bento-feed-grid"
          columnClassName="my-masonry-grid_column"
        >
          {displayedItems.map((item, index) => {
            if (item.type === "ad") {
              return (
                <div key={item.data.id} className="explore-bento-grid-item home-bento-feed-item home-bento-ad-item">
                  {item.data.render(item.data.id)}
                </div>
              );
            }

            if (item.type === "product") {
              return (
                <div key={`${item.type}-${item.data._id || index}`} className="explore-bento-grid-item home-bento-feed-item home-bento-product-item">
                  <ProductCard data={item.data} />
                </div>
              );
            }

            if (item.type === "event") {
              return (
                <div key={`${item.type}-${item.data._id || index}`} className="explore-bento-grid-item home-bento-feed-item home-bento-event-item">
                  <EventCard event={item.data} onRSVP={() => {}} onCancelRSVP={() => {}} />
                </div>
              );
            }

            return (
              <div key={`${item.type}-${item.data._id || index}`} className="explore-bento-grid-item home-bento-feed-item home-bento-post-item">
                <PostWidgetRedux postId={item.data._id} avatarSize="45px" {...item.data} />
              </div>
            );
          })}
        </Masonry>
      </InfiniteScroll>

      {!hasMoreItems && (
        <div className="bento-feed-end">
          <p>You've reached the end of the feed</p>
        </div>
      )}
    </div>
  );
};

export default BentoFeed;
